import { useState } from "react";
import {
  Check,
  Circle,
  AlertCircle,
  Clock,
  ListChecks,
  XCircle,
  FileText,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import "./AgenticTimeline.scss";
import ActionStepResult from "./ActionStepResult";
import MessageTextContainer from "../MessageTextContainer";
/**
 * Component to visualize agentic execution timeline as a flat list of steps and plan updates.
 */
const AgenticTimeline = ({ timeline, sessionStatus, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (!timeline || timeline.length === 0) {
    return null;
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const getStepStatusIcon = (stepStatus) => {
    switch (stepStatus) {
      case "DONE":
        return <Check size={16} color="#10b981" strokeWidth={2.5} />;
      case "VERIFYING":
        return <Clock size={16} color="#f59e0b" strokeWidth={2} />;
      case "INCOMPLETE":
        return <AlertCircle size={16} color="#f59e0b" strokeWidth={2} />;
      case "FAILED":
        return <XCircle size={16} color="#ef4444" strokeWidth={2} />;
      case "PLANNING":
        return <Clock size={16} color="#3b82f6" strokeWidth={2} />;
      default:
        return <Circle size={16} color="#d1d5db" strokeWidth={2} />;
    }
  };

  const getStepTypeLabel = (stepType) => {
    switch (stepType) {
      case "PLAN":
        return "Planning";
      case "DECIDE_NEXT_STEP":
        return "Deciding Next Step";
      case "ACTION":
        return "Action";
      case "VERIFY_STEP":
        return "Verifying Step";
      case "VERIFY_TASK":
        return "Verifying Task";
      case "FINAL_RESULT":
        return "Final Result";
      default:
        return stepType;
    }
  };

  const getTaskStatusBadge = (status) => {
    const badges = {
      COMPLETED: { label: "Completed", className: "status_completed" },
      PENDING: { label: "Pending", className: "status_pending" },
      FAILED: { label: "Failed", className: "status_failed" },
    };
    return badges[status] || { label: status, className: "status_default" };
  };

  return (
    <div className="agentic_timeline_flat">
      {/* Header */}
      <div
        className={`timeline_header ${!isExpanded ? 'collapsed' : ''}`}
        onClick={toggleExpanded}
      >
        <div className="timeline_header_left">
          {isExpanded ? (
            <ChevronDown size={20} color="#344767" strokeWidth={2} />
          ) : (
            <ChevronRight size={20} color="#344767" strokeWidth={2} />
          )}
          <div className="timeline_title">Agent Execution Timeline</div>
        </div>
        {sessionStatus && (
          <div className="timeline_status">
            <span className="status_badge">{sessionStatus}</span>
          </div>
        )}
      </div>

      {/* Timeline Entries */}
      {isExpanded && (
        <div className="timeline_entries">
          {timeline.map((entry, index) => {
          // Render plan update entry
          if (entry.type === "plan_update") {
            return (
              <div
                key={`plan-${index}`}
                className="timeline_entry plan_update_entry"
              >
                <div className="entry_icon">
                  <ListChecks size={16} color="#3b82f6" strokeWidth={2} />
                </div>
                <div className="entry_content">
                  <div className="entry_title">Plan Updated</div>
                  {entry.planTasks && entry.planTasks.length > 0 && (
                    <div className="plan_tasks_table">
                      {entry.planTasks.map((task) => {
                        const badge = getTaskStatusBadge(task.status);
                        return (
                          <div key={task.id} className="plan_task_row">
                            <div className="task_id">#{task.id}</div>
                            <div className="task_title">{task.title}</div>
                            <div className={`task_status ${badge.className}`}>
                              {badge.label}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // Render step entry
          if (entry.type === "step") {
            return (
              <div key={`step-${index}`} className="timeline_entry step_entry">
                <div className="entry_icon">
                  {getStepStatusIcon(entry.stepStatus)}
                </div>
                <div className="entry_content">
                  <div className="entry_meta">
                    <span className="step_type_label">
                      {getStepTypeLabel(entry.stepType)}
                    </span>
                    {entry.stepNumber && (
                      <span className="step_number">
                        Step #{entry.stepNumber}
                      </span>
                    )}
                    {entry.taskId && entry.taskText && (
                      <span className="task_context">
                        Task #{entry.taskId}: {entry.taskText}
                      </span>
                    )}
                  </div>
                  <div className="entry_title">{entry.title}</div>
                  {entry.content && (
                    <div className="entry_description">{entry.content}</div>
                  )}
                  {entry.stepResult &&
                    (entry.stepType === "VERIFY_STEP" ||
                      entry.stepType === "VERIFY_TASK") && (
                      <div className="entry_result">
                        <div className="result_label">
                          Recommendation:{" "}
                          {JSON.parse(entry.stepResult).recommendation}
                        </div>
                        <div className="result_text">
                          {JSON.parse(entry.stepResult).reasoning}
                        </div>
                      </div>
                    )}
                  {entry.stepResult && entry.stepType === "ACTION" && (
                    <ActionStepResult step={entry} />
                  )}
                  {entry.stepResult && entry.stepType === "FINAL_RESULT" && (
                    <div className="final_result_container">
                      <div className="final_result_icon">
                        <FileText size={18} color="#10b981" />
                      </div>
                      <div className="final_result_content">
                        <MessageTextContainer>
                          {entry.stepResult}
                        </MessageTextContainer>
                      </div>
                    </div>
                  )}
                  {entry.errorMessage && (
                    <div className="entry_error">
                      <AlertCircle size={14} />
                      <span>{entry.errorMessage}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // Render finish entry
          if (entry.type === "finish") {
            return (
              <div
                key={`finish-${index}`}
                className="timeline_entry finish_entry"
              >
                <div className="entry_icon">
                  {entry.sessionStatus === "COMPLETED" ? (
                    <Check size={16} color="#10b981" strokeWidth={2.5} />
                  ) : (
                    <XCircle size={16} color="#ef4444" strokeWidth={2} />
                  )}
                </div>
                <div className="entry_content">
                  <div className="entry_title">
                    {entry.sessionStatus === "COMPLETED"
                      ? "Session Completed"
                      : "Session Failed"}
                  </div>
                  {entry.errorMessage && (
                    <div className="entry_error">
                      <AlertCircle size={14} />
                      <span>{entry.errorMessage}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          }

          return null;
          })}
        </div>
      )}
    </div>
  );
};

export default AgenticTimeline;
